import { BlockCodePart } from "@redotech/redo-block-service-common/block-content";
import {
  BLOCK_SERVICE_PROTOCOL,
  BlockServiceTheme,
  parseOutgoingBlockServiceMessage,
} from "@redotech/redo-block-service-common/messages";

export const BLOCK_SERVICE_READY_FALLBACK_MS = 5000;
export const DEFAULT_BLOCK_SERVICE_ORIGIN = "http://localhost:3017";

export function blockServiceOrigin(): string {
  const configured = (globalThis as { REDO_BLOCK_SERVICE_URL?: string })
    .REDO_BLOCK_SERVICE_URL;
  return configured && configured.length > 0
    ? configured.replace(/\/$/, "")
    : DEFAULT_BLOCK_SERVICE_ORIGIN;
}

export function blockServiceSrc(
  origin: string,
  params: { frameId: string; blockId: string; embedder: string },
): string {
  return `${origin}/v1/block.html#${new URLSearchParams(params).toString()}`;
}

export interface MountBlockServiceOptions {
  container: HTMLElement;
  frameOrigin?: string;
  blockId: string;
  html: string;
  code?: BlockCodePart[];
  theme?: BlockServiceTheme;
  context: unknown;
  page: unknown;
  fallbackMs?: number;
  /** Sanitizes `html` for the no-`redo:ready` fallback; the host owns the allowlist. */
  sanitizeFallback: (html: string) => string;
  /** Class name for the fallback node, alongside `block-service-fallback`. */
  fallbackClassName?: string;
  onHeight?: (height: number) => void;
  onError?: (error: { kind: string; message?: string; url?: string }) => void;
}

export interface BlockServiceHandle {
  frameId: string;
  iframe: HTMLIFrameElement;
  updateContext(context: unknown): void;
  destroy(): void;
}

export function mountBlockFrame({
  container,
  frameOrigin = blockServiceOrigin(),
  blockId,
  html,
  code = [],
  theme = {},
  context,
  page,
  fallbackMs = BLOCK_SERVICE_READY_FALLBACK_MS,
  sanitizeFallback,
  fallbackClassName,
  onHeight,
  onError,
}: MountBlockServiceOptions): BlockServiceHandle {
  const frameId = crypto.randomUUID();
  const embedder = window.location.origin;
  const iframe = document.createElement("iframe");
  iframe.title = `Redo block ${blockId}`;
  iframe.setAttribute("data-block-id", blockId);
  // allow-same-origin is safe here: the frame is on a different origin, so it
  // grants nothing extra, but without it messages arrive as a "null" origin.
  // Links open in a new tab; without escaping the sandbox that tab inherits it
  // and the destination's forms (e.g. add to cart) cannot submit. No top
  // navigation: merchant or vendor code must never replace the tracking page.
  iframe.setAttribute(
    "sandbox",
    "allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox",
  );
  iframe.style.cssText =
    "width:100%;height:0;border:0;display:block;background:transparent;";
  iframe.src = blockServiceSrc(frameOrigin, { frameId, blockId, embedder });

  let ready = false;
  let latestContext = context;
  // Tracks whichever node currently lives in the container, so destroy() can
  // remove a fallback div that replaced the iframe.
  let mountedNode: HTMLElement = iframe;
  const lastHtml = html;
  const targetOrigin = new URL(frameOrigin).origin;

  const fallBack = () => {
    if (ready) {
      return;
    }
    const fallback = Object.assign(document.createElement("div"), {
      className: ["block-service-fallback", fallbackClassName]
        .filter(Boolean)
        .join(" "),
      innerHTML: sanitizeFallback(lastHtml),
    });
    mountedNode.replaceWith(fallback);
    mountedNode = fallback;
  };

  const onMessage = (event: MessageEvent) => {
    if (event.source !== iframe.contentWindow) {
      return;
    }
    if (event.origin !== targetOrigin) {
      return;
    }
    const parsed = parseOutgoingBlockServiceMessage(event.data);
    if (!parsed || parsed.frameId !== frameId) {
      return;
    }
    if (parsed.type === "redo:ready") {
      if (parsed.v !== BLOCK_SERVICE_PROTOCOL) {
        return;
      }
      ready = true;
      window.clearTimeout(fallbackTimer);
      iframe.contentWindow?.postMessage(
        {
          v: BLOCK_SERVICE_PROTOCOL,
          type: "redo:render",
          frameId,
          blockId,
          html: lastHtml,
          code,
          theme,
          context: latestContext,
          page,
        },
        event.origin,
      );
      return;
    }
    if (parsed.type === "redo:frame-height") {
      const height = Math.max(0, Number(parsed.height) || 0);
      iframe.style.height = `${height}px`;
      onHeight?.(height);
      return;
    }
    if (parsed.type === "redo:error") {
      onError?.({
        kind: parsed.kind,
        message: parsed.message,
        url: parsed.url,
      });
    }
  };

  window.addEventListener("message", onMessage);
  const fallbackTimer = window.setTimeout(fallBack, fallbackMs);
  container.append(iframe);

  return {
    frameId,
    iframe,
    updateContext(nextContext: unknown) {
      latestContext = nextContext;
      if (!ready || !iframe.contentWindow) {
        return;
      }
      iframe.contentWindow.postMessage(
        { type: "redo:context", frameId, context: nextContext },
        targetOrigin,
      );
    },
    destroy() {
      window.clearTimeout(fallbackTimer);
      window.removeEventListener("message", onMessage);
      mountedNode.remove();
    },
  };
}
